import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Shield, FileText, User, Phone, MapPin, Mail, Package,
  Plus, X, Clock, CheckCircle, AlertTriangle, Archive, RotateCcw,
  ChevronDown, ChevronUp, Save, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminCaseFiles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState("open");
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  useEffect(() => {
    if (isAdmin) loadCaseFiles();
  }, [filter, isAdmin]);

  const checkAdminAndLoad = async () => {
    try {
      const res = await fetch(`${API}/admin/check`, { headers: getAuthHeaders() });
      if (!res.ok) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
    } catch {
      navigate("/dashboard");
    }
  };

  const loadCaseFiles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/case-files?status=${filter}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setCaseFiles(await res.json());
      }
    } catch (err) {
      toast.error("Failed to load case files");
    } finally {
      setLoading(false);
    }
  };

  const openCaseDetail = async (caseFileId) => {
    try {
      const res = await fetch(`${API}/admin/case-files/${caseFileId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setSelectedCase(await res.json());
      }
    } catch {
      toast.error("Failed to load case file");
    }
  };

  if (selectedCase) {
    return (
      <CaseFileDetail
        caseFile={selectedCase}
        onBack={() => { setSelectedCase(null); loadCaseFiles(); }}
        onUpdate={(updated) => setSelectedCase(updated)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="back-to-admin-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-7 h-7 text-red-400" />
              <span className="font-heading text-xl font-bold uppercase tracking-wider">Case Files</span>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="create-case-file-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Case File
          </Button>
        </div>
      </header>

      {showCreateDialog && (
        <CreateCaseFileDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={(caseFileId) => {
            setShowCreateDialog(false);
            loadCaseFiles();
            openCaseDetail(caseFileId);
          }}
        />
      )}

      <main className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Filter tabs */}
        <div className="flex gap-2 mb-6" data-testid="case-filter-tabs">
          {[
            { key: "open", label: "Open", color: "text-red-400" },
            { key: "closed", label: "Archived", color: "text-gray-400" },
            { key: "all", label: "All", color: "text-primary" }
          ].map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}-btn`}
              className={filter === f.key ? "" : "border-border"}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading case files...</p>
          </div>
        ) : caseFiles.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground text-lg">No {filter !== "all" ? filter : ""} case files</p>
              <p className="text-sm text-muted-foreground mt-1">Case files are created automatically when a user reports a tool as stolen</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {caseFiles.map(cf => (
              <Card
                key={cf.case_file_id}
                className={`border-border bg-card cursor-pointer hover:border-red-500/50 transition-colors ${cf.status === "closed" ? "opacity-70" : ""}`}
                onClick={() => openCaseDetail(cf.case_file_id)}
                data-testid={`case-file-${cf.case_file_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-heading font-bold text-base">{cf.case_file_id}</span>
                        {cf.case_number && (
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">{cf.case_number}</Badge>
                        )}
                        <Badge className={cf.status === "open" ? "bg-red-500/20 text-red-400" : "bg-gray-500/20 text-gray-400"}>
                          {cf.status === "open" ? "Open" : "Archived"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <User className="w-3 h-3" />
                        <span>{cf.user_name}</span>
                        <span className="text-xs">({cf.user_email})</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-red-400 font-medium">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {cf.stolen_count} stolen
                        </span>
                        <span className="text-green-400 font-medium">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {cf.resolved_count} resolved
                        </span>
                        <span className="text-muted-foreground">
                          <Package className="w-3 h-3 inline mr-1" />
                          {cf.tools_count} total
                        </span>
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground shrink-0">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(cf.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


// ==================== CASE FILE DETAIL VIEW ====================

function CaseFileDetail({ caseFile: initialCaseFile, onBack, onUpdate }) {
  const [cf, setCf] = useState(initialCaseFile);
  const [caseNumber, setCaseNumber] = useState(cf.case_number || "");
  const [policeForces, setPoliceForces] = useState(cf.police_forces || "");
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newNoteDate, setNewNoteDate] = useState(new Date().toISOString().split("T")[0]);
  const [addingNote, setAddingNote] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showTools, setShowTools] = useState(true);
  const [showNotes, setShowNotes] = useState(true);

  const saveDetails = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/case-files/${cf.case_file_id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ case_number: caseNumber, police_forces: policeForces })
      });
      if (res.ok) {
        const updated = await res.json();
        setCf(prev => ({ ...prev, ...updated }));
        toast.success("Case file updated");
      } else {
        throw new Error();
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/admin/case-files/${cf.case_file_id}/notes`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ text: newNote, date: newNoteDate })
      });
      if (res.ok) {
        const data = await res.json();
        setCf(prev => ({ ...prev, notes: [...(prev.notes || []), data.note] }));
        setNewNote("");
        setNewNoteDate(new Date().toISOString().split("T")[0]);
        toast.success("Note added");
      }
    } catch {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const closeCase = async () => {
    setClosing(true);
    try {
      const res = await fetch(`${API}/admin/case-files/${cf.case_file_id}/close`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      if (res.ok) {
        toast.success("Case file closed and archived");
        onBack();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Cannot close case file");
      }
    } catch {
      toast.error("Failed to close case file");
    } finally {
      setClosing(false);
    }
  };

  const reopenCase = async () => {
    try {
      const res = await fetch(`${API}/admin/case-files/${cf.case_file_id}/reopen`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setCf(prev => ({ ...prev, status: "open", closed_at: null }));
        toast.success("Case file reopened");
      }
    } catch {
      toast.error("Failed to reopen");
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "Stolen": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "Active": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "Written Off": return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default: return "bg-gray-500/20 text-gray-300 border-gray-500/30";
    }
  };

  const allResolved = cf.tools?.every(t => t.status !== "Stolen");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="case-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Case Files
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-red-400" />
              <span className="font-heading text-lg font-bold uppercase tracking-wider">{cf.case_file_id}</span>
              <Badge className={cf.status === "open" ? "bg-red-500/20 text-red-400" : "bg-gray-500/20 text-gray-400"}>
                {cf.status === "open" ? "Open" : "Archived"}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
        {/* User Info */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Reporting User
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span>{cf.user_details?.name || cf.user_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{cf.user_details?.email || cf.user_email}</span>
            </div>
            {(cf.user_details?.phone || cf.user_phone) && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{cf.user_details?.phone || cf.user_phone}</span>
              </div>
            )}
            {(cf.user_details?.postcode || cf.user_postcode) && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{cf.user_details?.postcode || cf.user_postcode}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Case Details */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              Case Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Case / Crime Reference Number</Label>
              <Input
                value={caseNumber}
                onChange={e => setCaseNumber(e.target.value)}
                placeholder="e.g. CR-2026-12345"
                className="mt-1"
                data-testid="case-number-input"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Police Force(s) / Organisations</Label>
              <Textarea
                value={policeForces}
                onChange={e => setPoliceForces(e.target.value)}
                placeholder="e.g. West Yorkshire Police, British Transport Police"
                className="mt-1 min-h-[60px]"
                data-testid="police-forces-input"
              />
            </div>
            <Button
              onClick={saveDetails}
              disabled={saving}
              size="sm"
              className="bg-primary text-primary-foreground"
              data-testid="save-case-details-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Details
            </Button>
          </CardContent>
        </Card>

        {/* Stolen Tools */}
        <Card className="border-red-500/30 bg-card">
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowTools(!showTools)}>
            <CardTitle className="font-heading uppercase text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-red-400" />
                Tools in Case ({cf.tools?.length || 0})
              </div>
              {showTools ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {showTools && (
            <CardContent className="space-y-3">
              {cf.tools?.map(tool => (
                <div
                  key={tool.tool_id}
                  className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border"
                  data-testid={`case-tool-${tool.tool_id}`}
                >
                  {tool.image_url ? (
                    <img src={tool.image_url} alt="" className="w-14 h-14 rounded object-cover border border-border" />
                  ) : (
                    <div className="w-14 h-14 rounded bg-muted flex items-center justify-center">
                      <Package className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{tool.manufacturer} <span className="text-primary capitalize">{tool.tool_type}</span></p>
                    <p className="text-xs text-muted-foreground">ID: {tool.tool_id}</p>
                    <p className="text-xs text-muted-foreground">S/N: {tool.serial_number}</p>
                    {(tool.scan_price || tool.estimated_value) && (
                      <p className="text-xs text-primary font-medium">
                        Value: £{(tool.scan_price || tool.estimated_value || 0).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <Badge className={`text-xs border ${statusColor(tool.status)}`}>
                    {tool.status}
                  </Badge>
                </div>
              ))}
              {(!cf.tools || cf.tools.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No tools in this case file</p>
              )}
            </CardContent>
          )}
        </Card>

        {/* Notes */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowNotes(!showNotes)}>
            <CardTitle className="font-heading uppercase text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Notes ({cf.notes?.length || 0})
              </div>
              {showNotes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {showNotes && (
            <CardContent className="space-y-4">
              {/* Existing notes */}
              {cf.notes?.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cf.notes.map((note, idx) => (
                    <div key={idx} className="p-3 bg-background/50 rounded border border-border text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-primary font-medium">
                          {note.date ? new Date(note.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </span>
                        <span className="text-xs text-muted-foreground">{note.added_by}</span>
                      </div>
                      <p className="text-foreground">{note.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add note form */}
              {cf.status === "open" && (
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="flex gap-2">
                    <div className="w-36">
                      <Label className="text-xs text-muted-foreground">Date</Label>
                      <Input
                        type="date"
                        value={newNoteDate}
                        onChange={e => setNewNoteDate(e.target.value)}
                        className="mt-1 text-sm"
                        data-testid="note-date-input"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Note</Label>
                      <Textarea
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        placeholder="Add a case note..."
                        className="mt-1 min-h-[60px] text-sm"
                        data-testid="note-text-input"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={addNote}
                    disabled={addingNote || !newNote.trim()}
                    data-testid="add-note-btn"
                  >
                    {addingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Note
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Actions */}
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            {cf.status === "open" ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Close & Archive Case</p>
                  <p className="text-xs text-muted-foreground">
                    {allResolved
                      ? "All tools resolved. This case can be closed."
                      : "All tools must be Written Off or Recovered before this case can be closed."}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeCase}
                  disabled={closing || !allResolved}
                  className={allResolved ? "border-green-500 text-green-400 hover:bg-green-500/10" : "border-border text-muted-foreground"}
                  data-testid="close-case-btn"
                >
                  {closing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
                  Close Case
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Case archived on {cf.closed_at ? new Date(cf.closed_at).toLocaleDateString("en-GB") : "N/A"}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reopenCase}
                  className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                  data-testid="reopen-case-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reopen Case
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dates */}
        <div className="text-xs text-muted-foreground text-center space-y-1">
          <p>Created: {new Date(cf.created_at).toLocaleString("en-GB")}</p>
          <p>Last Updated: {new Date(cf.updated_at).toLocaleString("en-GB")}</p>
        </div>
      </main>
    </div>
  );
}


// ==================== CREATE CASE FILE DIALOG ====================

function CreateCaseFileDialog({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);

  useEffect(() => {
    loadUsersWithStolen();
  }, []);

  const loadUsersWithStolen = async () => {
    try {
      const res = await fetch(`${API}/admin/users-with-stolen`, { headers: getAuthHeaders() });
      if (res.ok) {
        setUsers(await res.json());
      }
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/case-files`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          tool_ids: selectedTools.length > 0 ? selectedTools : selectedUser.tool_ids
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Case file ${data.case_file_id} created`);
        onCreated(data.case_file_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create");
      }
    } catch {
      toast.error("Failed to create case file");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="create-case-dialog"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-base uppercase tracking-wider">Create Case File</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">Loading users with stolen tools...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">No users with stolen tools found</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">Select a user to create a case file for:</p>
              {users.map(u => (
                <div
                  key={u.user_id}
                  onClick={() => {
                    setSelectedUser(u);
                    setSelectedTools(u.tool_ids);
                  }}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedUser?.user_id === u.user_id
                      ? "border-red-500 bg-red-500/10"
                      : "border-border hover:border-red-500/50"
                  }`}
                  data-testid={`user-option-${u.user_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-500/20 text-red-400 text-xs">{u.stolen_count} stolen</Badge>
                      {u.has_open_case && (
                        <p className="text-xs text-amber-400 mt-1">Open case: {u.open_case_id}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!selectedUser || creating}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="confirm-create-case-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Case File
          </Button>
        </div>
      </div>
    </div>
  );
}
